import { useEffect, useRef } from 'react';
import api from '../api';

/**
 * Requests push notification permission and registers a Web Push subscription
 * with the backend. Should be called after the user has logged in.
 *
 * No-op in unsupported browsers or when the backend has no VAPID keys configured.
 */
export function usePushNotifications(enabled = false) {
  const subscribed = useRef(false);

  useEffect(() => {
    if (!enabled) return;
    if (subscribed.current) return;
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

    async function subscribe() {
      try {
        // 1. Fetch the VAPID public key — 503 means push not configured on backend
        const { data } = await api.get<{ publicKey: string }>('/push/vapid-public-key');
        const { publicKey } = data;

        // 2. Wait for an active SW registration
        const registration = await navigator.serviceWorker.ready;

        // 3. Check if already subscribed
        const existing = await registration.pushManager.getSubscription();
        if (existing) {
          // Re-register with backend in case of a new login
          await api.post('/push/subscribe', {
            endpoint: existing.endpoint,
            keys: {
              p256dh: btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(existing.getKey('p256dh')!)))),
              auth: btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(existing.getKey('auth')!)))),
            },
          });
          subscribed.current = true;
          return;
        }

        // 4. Request permission if needed
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return;

        // 5. Subscribe
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        });

        const json = subscription.toJSON();
        await api.post('/push/subscribe', {
          endpoint: json.endpoint,
          keys: json.keys,
        });

        subscribed.current = true;
      } catch {
        // Non-fatal — push is optional
      }
    }

    subscribe();
  }, [enabled]);
}

/** Convert a URL-safe base64 string to a Uint8Array (required by pushManager.subscribe) */
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  return Uint8Array.from(Array.from(rawData).map((c) => c.charCodeAt(0)));
}
